<script>
  import { onMount } from 'svelte';
  import { appStatus, pushNotice } from '../stores/ui.js';
  import { apiFetch, getApiBase } from '../lib/api/client.js';
  import { authState } from '../stores/auth.js';

  let adminToken = localStorage.getItem('ytcs:admin-token') || '';
  let users = [];
  let payments = [];
  let selectedUserId = '';
  let newUserEmail = '';
  let newUserPassword = '';
  let licensePlan = 'paid';
  let licenseActive = true;
  let licenseTrialEndsAt = '';
  let licenseExpiresAt = '';
  let paymentAmount = '';
  let paymentCurrency = 'EUR';
  let paymentStatus = 'paid';
  let paymentProvider = 'manual';
  let paymentNotes = '';

  onMount(() => {
    appStatus.set({
      primary: 'Admin',
      secondary: `Backend: ${getApiBase()}`
    });
    if (adminToken) {
      loadUsers();
    }
  });

  function saveAdminToken() {
    localStorage.setItem('ytcs:admin-token', adminToken);
    loadUsers();
  }

  function getAdminToken() {
    let token = adminToken;
    authState.subscribe((value) => {
      if (value.token && value.role === 'admin') token = value.token;
    })();
    return token;
  }

  async function loadUsers() {
    const token = getAdminToken();
    if (!token) return;
    try {
      const { users: payload } = await apiFetch('/admin/users', { token });
      users = payload;
      if (!selectedUserId && users.length) {
        selectUser(users[0]);
      }
    } catch (error) {
      pushNotice(error.message || 'Failed to load users.', 'error');
    }
  }

  async function createUser() {
    try {
      await apiFetch('/admin/users', {
        token: getAdminToken(),
        method: 'POST',
        body: { email: newUserEmail, password: newUserPassword || undefined }
      });
      newUserEmail = '';
      newUserPassword = '';
      pushNotice('User created.', 'success');
      await loadUsers();
    } catch (error) {
      pushNotice(error.message || 'Create user failed.', 'error');
    }
  }

  async function updateLicense() {
    if (!selectedUserId) return;
    try {
      await apiFetch(`/admin/licenses/${selectedUserId}`, {
        token: getAdminToken(),
        method: 'PATCH',
        body: {
          plan: licensePlan,
          active: licenseActive,
          trialEndsAt: licenseTrialEndsAt || null,
          expiresAt: licenseExpiresAt || null
        }
      });
      pushNotice('License updated.', 'success');
      await loadUsers();
    } catch (error) {
      pushNotice(error.message || 'License update failed.', 'error');
    }
  }

  async function deleteUser() {
    if (!selectedUserId) return;
    try {
      await apiFetch(`/admin/users/${selectedUserId}`, {
        token: getAdminToken(),
        method: 'DELETE'
      });
      selectedUserId = '';
      pushNotice('User deleted.', 'success');
      await loadUsers();
    } catch (error) {
      pushNotice(error.message || 'Delete user failed.', 'error');
    }
  }

  async function loadPayments(userId) {
    if (!userId) return;
    try {
      const { payments: payload } = await apiFetch(`/admin/payments?userId=${userId}`, {
        token: getAdminToken()
      });
      payments = payload;
    } catch (error) {
      pushNotice(error.message || 'Load payments failed.', 'error');
    }
  }

  async function createPayment() {
    if (!selectedUserId) return;
    try {
      await apiFetch('/admin/payments', {
        token: getAdminToken(),
        method: 'POST',
        body: {
          userId: selectedUserId,
          amount: Number(paymentAmount),
          currency: paymentCurrency,
          provider: paymentProvider,
          status: paymentStatus,
          notes: paymentNotes
        }
      });
      paymentAmount = '';
      paymentNotes = '';
      pushNotice('Payment added.', 'success');
      await loadPayments(selectedUserId);
    } catch (error) {
      pushNotice(error.message || 'Add payment failed.', 'error');
    }
  }

  async function deletePayment(id) {
    try {
      await apiFetch(`/admin/payments/${id}`, {
        token: getAdminToken(),
        method: 'DELETE'
      });
      await loadPayments(selectedUserId);
      pushNotice('Payment deleted.', 'success');
    } catch (error) {
      pushNotice(error.message || 'Delete payment failed.', 'error');
    }
  }

  function selectUser(user) {
    selectedUserId = user.id;
    licensePlan = user.license?.plan || 'trial';
    licenseActive = user.license?.active ?? true;
    licenseTrialEndsAt = user.license?.trialEndsAt || '';
    licenseExpiresAt = user.license?.expiresAt || '';
    loadPayments(user.id);
  }
</script>

<section class="panel">
  <div class="panel-title">Admin Token</div>
  <div class="row">
    <input placeholder="Admin token" bind:value={adminToken} />
    <button class="secondary" on:click={saveAdminToken}>Save</button>
  </div>
</section>

<section class="grid">
  <div class="panel">
    <div class="panel-title">Users</div>
    <div class="list">
      {#each users as user}
        <button class={`list-item ${selectedUserId === user.id ? 'active' : ''}`} on:click={() => selectUser(user)}>
          <div>{user.email}</div>
          <div class="muted">{user.license?.plan || 'trial'}</div>
        </button>
      {/each}
    </div>

    <div class="divider"></div>

    <div class="panel-title">Create user</div>
    <input placeholder="Email" bind:value={newUserEmail} />
    <input type="password" placeholder="Password (optional)" bind:value={newUserPassword} />
    <button class="secondary" on:click={createUser}>Create</button>
    <button class="danger" on:click={deleteUser}>Delete selected</button>
  </div>

  <div class="panel">
    <div class="panel-title">License</div>
    <label>Plan</label>
    <select bind:value={licensePlan}>
      <option value="trial">trial</option>
      <option value="paid">paid</option>
      <option value="paused">paused</option>
    </select>
    <label>Active</label>
    <select bind:value={licenseActive}>
      <option value={true}>true</option>
      <option value={false}>false</option>
    </select>
    <label>Trial ends</label>
    <input placeholder="YYYY-MM-DD" bind:value={licenseTrialEndsAt} />
    <label>Expires at</label>
    <input placeholder="YYYY-MM-DD" bind:value={licenseExpiresAt} />
    <button class="secondary" on:click={updateLicense}>Save license</button>
  </div>

  <div class="panel">
    <div class="panel-title">Payments</div>
    <div class="payment-list">
      {#each payments as payment}
        <div class="payment-row">
          <div>
            <div>{payment.amount} {payment.currency}</div>
            <div class="muted">{payment.status} · {payment.provider}</div>
          </div>
          <button class="ghost" on:click={() => deletePayment(payment._id)}>Delete</button>
        </div>
      {/each}
    </div>
    <div class="divider"></div>
    <input placeholder="Amount" bind:value={paymentAmount} />
    <input placeholder="Currency" bind:value={paymentCurrency} />
    <input placeholder="Provider" bind:value={paymentProvider} />
    <input placeholder="Status" bind:value={paymentStatus} />
    <input placeholder="Notes" bind:value={paymentNotes} />
    <button class="secondary" on:click={createPayment}>Add payment</button>
  </div>
</section>

<style>
  .panel {
    background: #fff;
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid rgba(31, 41, 51, 0.08);
    display: grid;
    gap: 0.75rem;
  }

  .panel-title {
    font-weight: 700;
  }

  .grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    margin-top: 1.5rem;
  }

  .row {
    display: flex;
    gap: 1rem;
  }

  input,
  select {
    padding: 0.6rem 0.8rem;
    border-radius: 12px;
    border: 1px solid rgba(31, 41, 51, 0.15);
    font-size: 0.9rem;
  }

  .secondary,
  .danger,
  .ghost {
    padding: 0.6rem 1.2rem;
    border-radius: 999px;
    border: none;
    cursor: pointer;
    font-weight: 600;
  }

  .secondary {
    background: rgba(31, 41, 51, 0.1);
  }

  .danger {
    background: #e53935;
    color: #fff;
  }

  .ghost {
    background: rgba(31, 41, 51, 0.08);
  }

  .list {
    display: grid;
    gap: 0.5rem;
  }

  .list-item {
    border: 1px solid rgba(31, 41, 51, 0.08);
    border-radius: 12px;
    padding: 0.6rem;
    text-align: left;
    background: rgba(255, 250, 241, 0.6);
    cursor: pointer;
    display: flex;
    justify-content: space-between;
  }

  .list-item.active {
    border-color: rgba(31, 41, 51, 0.3);
  }

  .muted {
    color: var(--muted);
    font-size: 0.8rem;
  }

  .divider {
    height: 1px;
    background: rgba(31, 41, 51, 0.12);
    margin: 0.5rem 0;
  }

  .payment-list {
    display: grid;
    gap: 0.5rem;
  }

  .payment-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
</style>
